/**
 * Cart backup and restore for back-navigation after brand switch
 * @module shared-cart/cart/backup
 */

import { CART_BACKUP_STORAGE_KEY, CART_BACKUP_MAX_AGE_MS, UserMessages } from '../constants.js';
import { getCart, addToCart, applyDiscountCode, refreshCartUI } from './api.js';
import { showRestoringState } from './sections.js';
import { displayError } from '../ui/error.js';
import { createSnapshotStore } from './snapshot.js';
import type { CartState, PreparedItem } from './api.js';
import type { SnapshotEntry } from './snapshot.js';

interface BackupItem extends PreparedItem {
  id: number;
  quantity: number;
  gwp?: true;
}

interface BackupEntry extends SnapshotEntry {
  timestamp: number;
  items: BackupItem[];
  discountCodes: string[];
}

const store = createSnapshotStore({
  storage: 'session',
  key: CART_BACKUP_STORAGE_KEY,
  mode: 'single',
  ttlMs: CART_BACKUP_MAX_AGE_MS,
  version: 1,
  timestampField: 'timestamp',
});

/**
 * Saves a backup of the current cart to sessionStorage.
 * Best-effort — silently catches storage errors.
 * @param {Object} cart - The full Shopify cart object from getCart()
 * @param {string[]} [discountCodes=[]] - Array of applicable discount code strings
 */
export function backupCart(cart: CartState, discountCodes: string[] = []): void {
  try {
    const items = cart.items.map((item): BackupItem => {
      const entry: BackupItem = {
        id: Number(item.variant_id),
        quantity: item.quantity!,
      };
      if (item.properties?._gwp === 'true' || item.properties?._gwp === true) {
        entry.gwp = true;
      }
      return entry;
    });
    const ok = store.save({ items, discountCodes });
    if (ok) {
      console.info('[Shared Cart] Cart backup saved:', { itemCount: items.length });
    }
  } catch (error) {
    console.warn('[Shared Cart] Failed to save cart backup:', error);
  }
}

/**
 * Reads and parses the cart backup from sessionStorage
 * @returns {Object|null} The parsed backup object or null
 */
export function getBackup(): BackupEntry | null {
  return store.get() as BackupEntry | null;
}

/**
 * Validates that a backup is well-formed and not expired
 * @param {Object} backup - The parsed backup object
 * @returns {boolean}
 */
export function isBackupValid(backup: unknown): backup is BackupEntry {
  if (!store.isValid(backup)) return false;
  if (!(backup as BackupEntry).items?.length) return false;
  return true;
}

/**
 * Removes the cart backup from sessionStorage
 */
export function clearBackup(): void {
  store.clear();
}

/**
 * Detects whether the current page load is a back/forward navigation
 * @param {PageTransitionEvent} event - The pageshow event
 * @returns {boolean}
 */
export function isBackNavigation(event: Pick<PageTransitionEvent, 'persisted'>): boolean {
  if (event.persisted) return true;
  const navEntry = performance.getEntriesByType?.('navigation')?.[0];
  return (navEntry as PerformanceNavigationTiming | undefined)?.type === 'back_forward';
}

/**
 * Restores the cart from a sessionStorage backup.
 * Called on back-navigation after a brand switch.
 * @returns {Promise<boolean>} Whether restoration was successful
 */
export async function restoreCart(): Promise<boolean> {
  const backup = getBackup();
  if (!backup || !isBackupValid(backup)) {
    clearBackup();
    return false;
  }

  try {
    const cart = await getCart();
    if (cart.items.length > 0) {
      console.info('[Shared Cart] Cart already has items, skipping restore');
      clearBackup();
      return false;
    }
  } catch (error) {
    console.warn('[Shared Cart] Failed to check current cart, skipping restore:', error);
    clearBackup();
    return false;
  }

  showRestoringState();

  let itemsRestored = false;

  try {
    const result = await addToCart(backup.items, { render: false });
    if (result?.errors) {
      console.error('[Shared Cart] Cart add returned errors during restore:', result.errors);
      displayError(UserMessages.RESTORE_FAILED);
    } else {
      itemsRestored = true;
      console.info('[Shared Cart] Cart items restored:', { itemCount: backup.items.length });
    }
  } catch (error) {
    console.error('[Shared Cart] Failed to restore cart items:', error);
    displayError(UserMessages.RESTORE_FAILED);
  }

  // ⚠️ TEMPORARY — Remove after April 30, 2026
  if (itemsRestored && backup.discountCodes?.length) {
    try {
      const codes = backup.discountCodes.filter(
        (code) => !code.toLowerCase().startsWith('birthday-'),
      );
      if (codes.length) {
        await applyDiscountCode(codes.join(','), { render: false });
        console.info('[Shared Cart] Discount codes restored:', codes);
      }
    } catch (error) {
      console.warn('[Shared Cart] Failed to restore discount codes:', error);
    }
  }

  try {
    await refreshCartUI();
  } catch (error) {
    console.warn('[Shared Cart] Failed to refresh cart UI after restore:', error);
  }

  clearBackup();
  return itemsRestored;
}
